import { User } from "oidc-client-ts";
import { CustomerNamespaceExtractor } from "./customer-namespace-extractor/customer-namespace-extractor";
import { readAuthServiceEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import {
  UserInfoSupportTicketExtractor,
  userInfoForSupportTicketTypes,
} from "./user-info-support-ticket-extractor/user-info-support-ticket-extractor";
import { decodeBase64String } from "@src/cloud-connect/utils/decode-base64-string";
import { trimWhitespacesAndQuotes } from "../../../common/trim-whitespaces-and-quotes";
import { getRolesPermissionsService } from "@src/cloud-connect/dependency-injection-container/get-roles-permissions-service";
import { getPermissionsSetService } from "@src/cloud-connect/dependency-injection-container/get-permissions-set-service";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { LoadPermissionsFromApi } from "@src/cloud-connect/roles-permissions/use-cases/load-permissions-from-api";

const ZITADEL_KEYS = {
  PROFILE_METADATA: "urn:zitadel:iam:user:metadata",
  PROJECT_ROLES: "urn:zitadel:iam:org:project:roles",
  ORGANIZATION_NAME: "tomorrowthings-customer-organization-name",
} as const;

const DEFAULT_ORGANIZATION = "unknown Organization";

export interface AuthContext {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  customerNamespace: string;
  userInfoForSupportTicket: userInfoForSupportTicketTypes;
  fullName: string;
  organization: string;
  permissions: string[];
  userId: string;
  zitadelConsoleUrl: string;
}

const extractCustomerNamespace = (user: User): string => {
  const extractor = new CustomerNamespaceExtractor();
  extractor.setStrategy(readAuthServiceEnvVar());

  const namespace = extractor.extractCustomerNamespaceFromUser(user);
  return trimWhitespacesAndQuotes(namespace);
};

const extractOrganization = (user: User): string => {
  const metadata = user?.profile?.[ZITADEL_KEYS.PROFILE_METADATA]?.[0];
  const encodedOrgName = metadata?.[ZITADEL_KEYS.ORGANIZATION_NAME];

  if (!encodedOrgName) {
    return DEFAULT_ORGANIZATION;
  }

  const decodedName = decodeBase64String(encodedOrgName);
  return trimWhitespacesAndQuotes(decodedName);
};

const extractRoles = (user: User): string[] => {
  const rolesObject = user?.profile?.[ZITADEL_KEYS.PROJECT_ROLES]?.[0];
  return rolesObject ? Object.keys(rolesObject) : [];
};

/**
 * Extracts user info needed for support tickets
 */
const extractUserInfoForSupportTicket = (
  user: User,
  customerNamespace: string,
): userInfoForSupportTicketTypes => {
  const extractor = new UserInfoSupportTicketExtractor(customerNamespace);
  return extractor.extractUserInfo(user);
};

const loadPermissions = async (roles: string[]): Promise<string[]> => {
  const permissionSetService = getPermissionsSetService();
  const rolesPermissionsService = getRolesPermissionsService();

  // check if permissions are already loaded
  const existingPermissions = permissionSetService.getPermissionsSet();
  if (existingPermissions.size > 0) return Array.from(existingPermissions);

  const loadPermissionsInstance = new LoadPermissionsFromApi(
    rolesPermissionsService,
    permissionSetService,
    roles,
  );

  const success = await loadPermissionsInstance.execute();

  if (!success) {
    navigateTo(Route.SERVER_ERROR);
    return [];
  }

  return Array.from(permissionSetService.getPermissionsSet());
};

const buildFullName = (user: User): string => {
  const { given_name = "", family_name = "" } = user.profile;
  return `${given_name} ${family_name}`.trim();
};

const buildZitadelConsoleUrl = (user: User): string => {
  return `${user.profile.iss}/ui/console`;
};

/**
 * Creates an authentication context from a user object
 * @param user - The OIDC user object
 * @returns AuthContext or null if user is not provided
 */
export async function getAuthContext(
  user: User | null | undefined,
): Promise<AuthContext | null> {
  if (!user) {
    return null;
  }

  // extract user information
  const customerNamespace = extractCustomerNamespace(user);
  const organization = extractOrganization(user);
  const roles = extractRoles(user);
  const userInfoForSupportTicket = extractUserInfoForSupportTicket(
    user,
    customerNamespace,
  );

  const permissions = await loadPermissions(roles);

  // build and return auth context
  return {
    idToken: user.id_token,
    accessToken: user.access_token,
    refreshToken: user.refresh_token,
    customerNamespace,
    userInfoForSupportTicket,
    fullName: buildFullName(user),
    organization,
    permissions,
    userId: user.profile.sub,
    zitadelConsoleUrl: buildZitadelConsoleUrl(user),
  };
}
